import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Bell, Calendar, ChefHat, FileText, LogOut, Menu, Trash2, User, UtensilsCrossed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/context/AuthContext';
import { getMsUntilMidnightRome } from '@/lib/utils';
import { APP_BASE } from '@/lib/api';

export const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const midnightTimeoutRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${APP_BASE}/api/admin/notifications/unread-count?today_only=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token]);

  useEffect(() => {
    const scheduleMidnightReset = () => {
      const msUntilMidnight = getMsUntilMidnightRome();

      midnightTimeoutRef.current = setTimeout(() => {
        setUnreadCount(0);
        fetchUnreadCount();
        scheduleMidnightReset();
      }, msUntilMidnight);
    };

    scheduleMidnightReset();

    return () => {
      if (midnightTimeoutRef.current) {
        clearTimeout(midnightTimeoutRef.current);
      }
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Calendario', icon: Calendar },
    { path: '/admin/notifications', label: 'Notifiche', icon: Bell, badge: unreadCount },
    { path: '/admin/menus', label: 'Disponibilita', icon: Calendar },
    { path: '/admin/full-menu', label: 'Menu Completo', icon: UtensilsCrossed },
    { path: '/admin/about-us', label: 'Chi siamo', icon: FileText },
    { path: '/admin/orders-trash', label: 'Cestino Ordini', icon: Trash2 },
    { path: '/admin/account', label: 'Account', icon: User },
  ];

  const NavItem = ({ item, onClick }) => (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-4 py-3 transition-colors ${
          isActive ? 'gradient-instagram text-primary-foreground' : 'text-foreground hover:bg-muted'
        }`
      }
    >
      <item.icon className="h-5 w-5" />
      <span className="flex-1">{item.label}</span>
      {item.badge > 0 && (
        <Badge variant="destructive" className="ml-auto">
          {item.badge}
        </Badge>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col border-r border-border bg-card">
        <div className="flex items-center gap-3 border-b border-border px-6 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full gradient-instagram">
            <ChefHat className="h-5 w-5 text-primary-foreground" />
          </div>

          <div>
            <h1 className="font-bold text-foreground">Sapori 1989</h1>
            <p className="text-xs text-muted-foreground">Area Admin</p>
          </div>
        </div>

        <nav className="flex-1 space-y-1 px-4 py-4">
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>

        <div className="border-t border-border px-4 py-4">
          <div className="mb-3 flex items-center gap-3 px-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              <User className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{user?.email || 'Admin'}</p>
            </div>
          </div>

          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      <header className="fixed left-0 right-0 top-0 z-50 border-b border-border bg-card px-4 py-3 lg:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full gradient-instagram">
              <ChefHat className="h-4 w-4 text-primary-foreground" />
            </div>

            <span className="font-bold text-foreground">Admin</span>

            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1">
                {unreadCount}
              </Badge>
            )}
          </div>

          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>

            <SheetContent side="right" className="w-64 p-0">
              <SheetTitle className="sr-only">Menu Admin</SheetTitle>

              <div className="flex h-full flex-col">
                <div className="flex items-center gap-3 border-b border-border px-6 py-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full gradient-instagram">
                    <ChefHat className="h-5 w-5 text-primary-foreground" />
                  </div>

                  <div>
                    <h1 className="font-bold text-foreground">Sapori 1989</h1>
                    <p className="text-xs text-muted-foreground">Area Admin</p>
                  </div>
                </div>

                <nav className="flex-1 space-y-1 px-4 py-4">
                  {navItems.map((item) => (
                    <NavItem key={item.path} item={item} onClick={() => setIsMobileMenuOpen(false)} />
                  ))}
                </nav>

                <div className="border-t border-border px-4 py-4">
                  <Button variant="outline" className="w-full" onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="pt-14 lg:pl-64 lg:pt-0">{children}</main>
    </div>
  );
};
